import { EmptyState } from '@/components/ui/empty-state';
import { Search, UserPlus } from 'iconoir-react';
import type React from 'react';
import { useTranslation } from 'react-i18next';

interface InvitationsEmptyStateProps {
  /**
   * `true` when the list is empty because the active search / status filter
   * matches nothing, `false` when no invitation exists at all. The two read
   * differently: a filtered list keeps its filter bar so the user can clear it,
   * an untouched program has nothing to filter in the first place.
   */
  filtered?: boolean;
  /**
   * CTA for the never-invited state. Deliberately ignored when `filtered`: the
   * way out of a filter that matches nothing is to clear the filter, not to
   * start an invitation.
   */
  action?: React.ReactNode;
}

const ICON_SIZE = 48;
const ICON_STROKE = 1.8;

/**
 * Empty state for the affiliate invitations list. Renders on its own, without
 * table headers or a pagination bar, so an empty list reads as a single message
 * instead of an empty grid.
 */
const InvitationsEmptyState: React.FC<InvitationsEmptyStateProps> = ({
  filtered = false,
  action,
}) => {
  const { t } = useTranslation();

  if (filtered) {
    return (
      <EmptyState
        icon={<Search width={ICON_SIZE} height={ICON_SIZE} strokeWidth={ICON_STROKE} />}
        title={t('invitations.noResults')}
        description={t('invitations.noResultsDescription')}
      />
    );
  }

  return (
    <EmptyState
      icon={<UserPlus width={ICON_SIZE} height={ICON_SIZE} strokeWidth={ICON_STROKE} />}
      title={t('invitations.noInvitations')}
      description={t('invitations.noInvitationsDescription')}
    >
      {action}
    </EmptyState>
  );
};

export default InvitationsEmptyState;
